import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Fuel, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { DashCard, DashTooltip, compactMoney, money } from './DashCard';

const LINE_COLORS = ['#2563eb', '#93c5fd', '#818cf8', '#0ea5e9', '#94a3b8'];

const SORTS = [
  { key: 'total_fuel', label: 'Spend', fmt: (r) => money(r.total_fuel) },
  { key: 'fuel_pct', label: '% of Gross', fmt: (r) => (r.fuel_pct == null ? '—' : `${r.fuel_pct}%`) },
];

const shortDate = (v) => {
  const [, m, d] = (v || '').split('-');
  return m ? `${Number(m)}/${Number(d)}` : v;
};

const Change = ({ pct, testid }) => {
  if (pct === null || pct === undefined) return <span className="text-slate-500 dark:text-slate-300" data-testid={testid}>—</span>;
  const up = pct > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${up ? 'text-rose-500' : 'text-blue-600 dark:text-blue-400'}`} data-testid={testid}>
      <Icon className="h-3.5 w-3.5" />{Math.abs(pct)}%
    </span>
  );
};

const SortToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full bg-slate-100 dark:bg-slate-800 p-0.5" data-testid="fuel-sort-toggle">
    {SORTS.map((s) => (
      <button
        key={s.key}
        type="button"
        onClick={() => onChange(s.key)}
        className={`px-3 py-1 text-sm font-semibold rounded-full transition-colors ${value === s.key ? 'bg-slate-900 text-white dark:bg-blue-600' : 'text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-200'}`}
        data-testid={`fuel-sort-${s.key}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

/** Per-truck fuel spend from weekly fuel deductions — trend of the top 5 + ranked table. */
export const FuelCard = ({ data }) => {
  const [sortKey, setSortKey] = useState('total_fuel');
  const rows = useMemo(
    () => [...(data?.units || [])].sort((a, b) => (b[sortKey] ?? -1) - (a[sortKey] ?? -1)),
    [data, sortKey],
  );
  const top = rows.slice(0, 5);
  const chartData = (data?.weeks || []).map((w, i) => {
    const point = { week_start: w };
    top.forEach((r) => { point[`u_${r.unit_id}`] = r.weekly[i]; });
    return point;
  });
  const sort = SORTS.find((s) => s.key === sortKey);
  const fleet = data?.fleet || {};

  return (
    <DashCard
      title="Fuel by Truck"
      subtitle={`Fuel deductions · last 12 weeks · fleet ${money(fleet.total)}${fleet.fuel_pct != null ? ` (${fleet.fuel_pct}% of gross)` : ''}`}
      icon={Fuel}
      delay={160}
      testid="dashboard-fuel-card"
      action={<SortToggle value={sortKey} onChange={setSortKey} />}
    >
      {!data ? (
        <div className="h-[280px] rounded-xl bg-slate-100 dark:bg-slate-800/50 animate-pulse" data-testid="fuel-loading" />
      ) : rows.length === 0 ? (
        <div className="h-[240px] flex items-center justify-center text-sm text-slate-500 dark:text-slate-300" data-testid="fuel-empty">
          No fuel deductions recorded in the last 12 weeks
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-2">
            <div className="flex items-baseline gap-3 mb-2" data-testid="fuel-fleet-week">
              <span className="text-sm uppercase tracking-[0.14em] text-slate-500 dark:text-slate-300">Fleet this week</span>
              <span className="font-mono text-lg font-semibold">{money(fleet.this_week)}</span>
              <span className="text-sm"><Change pct={fleet.change_pct} testid="fuel-fleet-change" /> <span className="text-slate-500 dark:text-slate-300">vs last wk</span></span>
            </div>
            <div className="h-[280px]" data-testid="fuel-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="4 6" stroke="currentColor" className="text-slate-200 dark:text-slate-800" />
                  <XAxis dataKey="week_start" tickFormatter={shortDate} tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compactMoney} tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} width={56} />
                  <Tooltip content={<DashTooltip labelFormatter={(v) => `Week of ${shortDate(v)}`} />} />
                  {top.map((r, i) => (
                    <Line
                      key={r.unit_id}
                      type="monotone"
                      dataKey={`u_${r.unit_id}`}
                      name={`Unit ${r.unit_number}`}
                      stroke={LINE_COLORS[i]}
                      strokeWidth={i === 0 ? 2.75 : 2}
                      dot={false}
                      activeDot={{ r: 4, strokeWidth: 0 }}
                      animationDuration={900}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-slate-600 dark:text-slate-300">
              {top.map((r, i) => (
                <span key={r.unit_id} className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: LINE_COLORS[i] }} /> Unit {r.unit_number}
                </span>
              ))}
            </div>
          </div>

          <div className="xl:col-span-3 overflow-x-auto max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="fuel-table">
              <thead className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                <tr className="text-sm uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  <th className="text-left font-medium pb-2 w-6">#</th>
                  <th className="text-left font-medium pb-2">Truck</th>
                  <th className="text-right font-medium pb-2">12-wk Spend</th>
                  <th className="text-right font-medium pb-2">% Gross</th>
                  <th className="text-right font-medium pb-2">$ / Mile</th>
                  <th className="text-right font-medium pb-2">This Wk</th>
                  <th className="text-right font-medium pb-2">WoW</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.unit_id} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`fuel-row-${i}`}>
                    <td className="py-2.5 font-mono text-slate-500 dark:text-slate-300">{i + 1}</td>
                    <td className="py-2.5 pr-2">
                      <div className="font-semibold text-slate-800 dark:text-slate-100 whitespace-nowrap">Unit {r.unit_number}</div>
                      <div className="text-sm text-slate-500 dark:text-slate-300 truncate max-w-[140px]">{r.driver_name || 'Unassigned'}</div>
                    </td>
                    {SORTS.map((s) => (
                      <td
                        key={s.key}
                        className={`py-2.5 text-right font-mono ${s.key === sortKey ? 'font-semibold text-blue-700 dark:text-blue-400' : 'text-slate-600 dark:text-slate-300'}`}
                        data-testid={`fuel-row-${i}-${s.key}`}
                      >
                        {s.fmt(r)}
                      </td>
                    ))}
                    <td className="py-2.5 text-right font-mono">{money(r.this_week)}</td>
                    <td className="py-2.5 text-right"><Change pct={r.change_pct} testid={`fuel-row-${i}-change`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-sm text-slate-500 dark:text-slate-300 mt-2">
              Ranked by {sort.label.toLowerCase()}. WoW in red means fuel spend went up vs last week.
            </p>
          </div>
        </div>
      )}
    </DashCard>
  );
};
